import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { getPublisherTagInstance } from '../utils/getPublisherTagInstance';
import { loadGpt } from '../utils/gptLoader';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { type AdSizes, type SlotRenderEndedEvent } from '../types';
import { type OnAbsentAd } from '../hooks/useAbsentAdHandler';

type Props = {
    adId: string;
    elementId: string;
    width: number;
    height: number;
    sizes?: AdSizes;
    heightHold?: number;
    widthHold?: number;
    className?: string;
    onSlotRenderEnded?: (event: SlotRenderEndedEvent, element: HTMLDivElement) => void;
    onAbsentAd?: OnAbsentAd;
    lazyLoad?: boolean;
    shouldCollapseOnNoFill?: boolean;
    dataTn?: string;
};

type GptLoadState = 'initial' | 'loaded' | 'failed';

export const AdUnit: FC<Props> = ({
    adId,
    elementId,
    width,
    height,
    className,
    onSlotRenderEnded,
    onAbsentAd,
    lazyLoad = false,
    sizes,
    heightHold,
    widthHold,
    shouldCollapseOnNoFill = false,
    dataTn,
}) => {
    const adRef = useRef<HTMLDivElement>(null);

    const [gptLoadState, setGptLoadState] = useState<GptLoadState>('initial');
    const [adSlot, setAdSlot] = useState<googletag.Slot | null>(null);
    const [isAdSlotRendered, setIsAdSlotRendered] = useState(false);

    const triggerGptLoad = useCallback(async (): Promise<void> => {
        try {
            await loadGpt();
            setGptLoadState('loaded');
        } catch {
            setGptLoadState('failed');
            onAbsentAd?.(elementId);
        }
    }, [onAbsentAd, elementId]);

    useEffect(() => {
        return () => {
            if (adSlot) {
                const gt = getPublisherTagInstance();
                gt?.destroySlots([adSlot]);
            }
        };
    }, [adSlot]);

    useEffect(() => {
        if (!lazyLoad) {
            triggerGptLoad();
        }
    }, [lazyLoad, triggerGptLoad]);

    useEffect(() => {
        const gt = getPublisherTagInstance();
        const onSlotRenderEndedHandler = (event: SlotRenderEndedEvent): void => {
            if (adRef.current) {
                setIsAdSlotRendered(true);
                onSlotRenderEnded?.(event, adRef.current);
                if (event.isEmpty) {
                    onAbsentAd?.(event.slot.getSlotElementId());
                }
            }
        };

        gt?.pubads().addEventListener('slotRenderEnded', onSlotRenderEndedHandler);

        if (gptLoadState === 'loaded' && !adSlot) {
            gt?.cmd.push(() => {
                gt?.pubads();

                // define ad slot
                const slot = gt.defineSlot(adId, sizes ? sizes : [width, height], elementId);
                if (slot) {
                    // configure ad slot
                    slot.addService(gt.pubads());
                    if (shouldCollapseOnNoFill) {
                        slot.setConfig({ collapseDiv: 'ON_NO_FILL' });
                    }
                }

                if (!gt.pubadsReady) {
                    gt.pubads().enableSingleRequest();
                    gt.enableServices();
                }

                setAdSlot(slot);
            });

            gt?.cmd.push(() => {
                gt?.display(elementId);
            });
        }
        return () => {
            gt?.pubads()?.removeEventListener?.('slotRenderEnded', onSlotRenderEndedHandler);
        };
    }, [
        gptLoadState,
        adId,
        elementId,
        adSlot,
        onSlotRenderEnded,
        onAbsentAd,
        sizes,
        width,
        height,
        shouldCollapseOnNoFill,
    ]);

    if (gptLoadState === 'failed') {
        return null;
    }

    return (
        <>
            {!!lazyLoad && (
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            triggerGptLoad();
                        }
                    }}
                    elementRef={adRef}
                    observerOptions={{
                        rootMargin: `200% 0px`, // Render slots within 2 viewports.
                    }}
                />
            )}
            <div
                style={!isAdSlotRendered ? { minHeight: heightHold || height } : {}}
                className={className}
                id={elementId}
                data-tn={dataTn || 'ad-unit'}
                data-target-width={widthHold || width}
                data-target-height={heightHold || height}
                ref={adRef}
            />
        </>
    );
};
